import { ApiError } from '@rr-crm/shared/api-errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantHistoryMessage,
  AssistantHistoryResponse,
} from '@rr-crm/shared/schemas/assistant';

import { apiFetch, throwIfNotOk } from '../api/client';

const HISTORY_QUERY_KEY = ['assistant', 'history'] as const;
const STATUS_QUERY_KEY = ['assistant', 'status'] as const;

/**
 * RIG-389 — whether the assistant is configured in this environment. Production
 * has no Azure OpenAI resource, so its chat would always 503; the "Ask Howie"
 * entry + route key off this. Cached for the session (staleTime Infinity).
 */
export function useAssistantStatus() {
  return useQuery<boolean>({
    queryKey: STATUS_QUERY_KEY,
    queryFn: async () => {
      const resp = await apiFetch('/api/v1/assistant/status');
      if (!resp.ok) return false;
      return ((await resp.json()) as { enabled: boolean }).enabled;
    },
    staleTime: Infinity,
  });
}

/**
 * Boolean convenience for nav gating: `false` while loading or on error, so the
 * entry only appears once the backend confirms the assistant is usable.
 */
export function useAssistantEnabled(): boolean {
  return useAssistantStatus().data ?? false;
}

/**
 * T-AI-RAG-003 — chat mutation hook. `apiFetch` attaches the MSAL bearer token,
 * so the assistant endpoint receives an authenticated, RBAC-scoped request.
 */
export function useAssistantChat() {
  return useMutation<AssistantChatResponse, Error, AssistantChatRequest>({
    mutationFn: async (input) => {
      const resp = await apiFetch('/api/v1/assistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (resp.status === 503) {
        throw new ApiError({
          status: 503,
          code: 'service_unavailable',
          message: 'The assistant is not configured yet. Check back soon.',
        });
      }
      await throwIfNotOk(resp);
      return (await resp.json()) as AssistantChatResponse;
    },
  });
}

/**
 * RIG-364 — load the signed-in user's saved Ask-Howie history (self-scoped
 * server-side). Used to seed the chat on mount so the conversation survives
 * reload/navigation.
 */
export function useAssistantHistory() {
  return useQuery<AssistantHistoryMessage[]>({
    queryKey: HISTORY_QUERY_KEY,
    queryFn: async () => {
      const resp = await apiFetch('/api/v1/assistant/history');
      await throwIfNotOk(resp);
      return ((await resp.json()) as AssistantHistoryResponse).messages;
    },
    staleTime: 60_000,
  });
}

/** RIG-364 — clear the signed-in user's history (soft-delete; self-scoped). */
export function useClearAssistantHistory() {
  const qc = useQueryClient();
  return useMutation<number, Error, void>({
    mutationFn: async () => {
      const resp = await apiFetch('/api/v1/assistant/history', { method: 'DELETE' });
      await throwIfNotOk(resp);
      return ((await resp.json()) as { cleared: number }).cleared;
    },
    onSuccess: () => {
      qc.setQueryData(HISTORY_QUERY_KEY, []);
    },
  });
}
